// Internal modules
import { appStore, dispatchFormAction } from '@/store';
import {
  logBankPaymentSuppressionSubmitBlockedToFpti,
  logCheckMemberBenefitsOccured,
  logOverCapturesDataOnSubmit,
  logPayLaterContinueButtonSubmittedToFpti,
  logPayLaterTermsCheckedToFpti,
  logProcessBillingPageToFpti,
} from './fpti';
import {
  checkoutGuestAsMember,
  createOnboardingPaymentPlan,
  onboardAction,
} from './graphql';
import {
  filterFormState,
  getFieldData,
  isEligibleForCRS,
  kycNationalityOnChange,
  logBlurOrFocusEvent,
  shouldDisplayErrorOnChange,
  shouldShowSCAPhoneConfirmationInterstitial,
  triggerMemberBenefitsTrueGuestsInterstitial,
  validateForm,
} from './utils';
import { checkMemberBenefits, serializeDataForMemberBenefits } from './util';
import {
  CI,
  features,
  focusOnFirstError,
  getFieldError,
  handleApiError,
  INPUT_FIELD_ERROR_STATUS_PAYLOAD,
  logger,
} from '@/lib';
import { threeDSHandler } from './threeDSHandler';
import { SHIPPING_CALLBACK_ERROR_MAPPING } from '@/lib/utils/shipping/constants';
import {
  atomics,
  atomicsDomain,
  atomicsErrorContexts,
  atomicsErrorReason,
  atomicsStatus,
} from '@/lib/atomics';

import { isAddressNormalized } from '@/components/Interstitials/AddressNormalizationInterstitial/api';
import { validateCRSFormFields } from '../CRSFields/handlers';

/**
 * @TECH_DEBT This function used to have more logic but it has been
 * removed. When we refactor the state management to use Apollo Client
 * we should go through all the places where this function, `blurHandler`
 * is used and swap it to reference `logBlurOrFocusEvent`
 */
const blurHandler = e => {
  logBlurOrFocusEvent(e);
};

const changeHandler = (e, value, oldValue, meta = {}) => {
  e.persist?.();

  // Using id instead of name attribute for consistency in state names since
  // some name attributes have dashes to be compatible with autocomplete
  const { id, checked: isChecked, type: fieldType } = e.target;
  const { addressType, currentState, field } = getFieldData(e.target, meta);
  const { errors = [], isValid } = meta.validation || {};
  const errorCode = errors[0] || '';
  const isCheckbox = fieldType === 'checkbox';

  /**
   * Display the error if we are currently displaying the error and the
   * field is invalid. We check the `displayError` property because
   * we don't want to show the error when the user first starts filling
   * out the field. For example, when the user enters the first character
   * into the email field it will be invalid because it doesn't match
   * the pattern for an email but immediately showing the error will
   * confuse the user.
   */
  const shouldDisplayError = shouldDisplayErrorOnChange(id, currentState, meta);
  const payload = {
    displayError: shouldDisplayError,
    error: getFieldError({ errorCode, field, isValid, value }),
    errorCode,
    isDirty: true,
    isValid,
    value: isCheckbox ? isChecked : value,
  };

  // Log the input error if we are displaying or removing the error
  // or if the error is displayed and the error code changes
  const isErrorStatusChange = shouldDisplayError !== currentState.displayError;
  const isErrorCodeChange = errorCode !== currentState.errorCode;
  if (isErrorStatusChange || (isErrorCodeChange && currentState.displayError)) {
    const logPayload = INPUT_FIELD_ERROR_STATUS_PAYLOAD(id, isValid, errorCode);
    logger.fpti(`${isValid ? 'clear' : 'set'}_input_error`, logPayload);
  }

  // Do not set cardType if CardTypeSelector component is enabled
  // Reason: cardNumber.cardType state will be updated by CardTypeSelector
  const { ui: uiState } = appStore.getState();
  if (field === 'cardNumber' && !uiState.hasCardTypeSelector) {
    payload.cardType = meta.cardType;
  }

  if (field === 'nationality' && value !== oldValue) {
    kycNationalityOnChange({ addressType, nationality: value });
  }

  dispatchFormAction({ addressType, field, payload });
};

const payLaterTermsCheckedHandler = (e, value, oldValue, meta) => {
  const { checked: isChecked } = e.target;

  if (isChecked) {
    logPayLaterTermsCheckedToFpti();
  }

  changeHandler(e, value, oldValue, meta);
};
const displayFormErrors = (fieldsWithErrors, formErrors) => {
  const { form: formActions } = appStore.getActions();
  const addressRegex = /^(billing|shipping|residential)Address$/;
  fieldsWithErrors.forEach(([field, payload]) => {
    const isAddressField = addressRegex.test(field);
    const fieldName = isAddressField ? 'addressType' : 'field';
    const action = isAddressField
      ? 'updateAddressField'
      : 'updateNonAddressField';

    formActions[action]({
      [fieldName]: field,
      payload,
      ...(isAddressField && { isBatchUpdate: true }),
    });
  });

  focusOnFirstError(formErrors);
};

const payNowSubmitHandler = e => {
  return submitHandler(e, logProcessBillingPageToFpti);
};

const payLaterSubmitHandler = e => {
  return submitHandler(e, logPayLaterContinueButtonSubmittedToFpti);
};

// Check if page level error should block form submission
const hasPageLevelError = pageLevelError => {
  let logMessage = '';
  let atomicsContext = '';

  switch (pageLevelError) {
    case 'pageLevelError.shippingRegionUnsupported':
      logMessage =
        'weasley_invalid_form_submission_due_to_unsupported_shipping_region';
      atomicsContext = atomicsErrorContexts.shippingRegionUnsupported;
      break;
    case 'pageLevelError.cannotUseBank':
      logMessage =
        'weasley_invalid_form_submission_due_to_bank_payment_suppressed';
      atomicsContext = 'bank_payment_suppressed';
      logBankPaymentSuppressionSubmitBlockedToFpti();
      break;
    default: {
      // Check for shipping callback errors (multiple possible values)
      const shippingCallbackErrors = Object.values(
        SHIPPING_CALLBACK_ERROR_MAPPING,
      );
      if (shippingCallbackErrors.includes(pageLevelError)) {
        logMessage =
          'weasley_invalid_form_submission_due_to_shipping_callback_error';
        atomicsContext = `${atomicsErrorContexts.shippingCallback}: ${pageLevelError}`;
        break;
      }

      return false;
    }
  }

  logger.warn(logMessage);
  window.scrollTo(0, 0);
  CI.error(CI.ErrorReason.User);
  CI.endCurrent();
  atomics.markProblem({
    context: atomicsContext,
    domain: atomicsDomain,
    reason: atomicsErrorReason.user,
    status: atomicsStatus.error,
  });

  return true;
};

const submitHandler = async (e, logFormSubmittedFunction) => {
  e.preventDefault();
  const { form: formState, ui: uiState } = appStore.getState();
  const { ui: uiActions, api: apiActions } = appStore.getActions();

  // Prevent form submission if submission is in progress
  if (uiState.isFormSubmitting) {
    return;
  }

  const hasError = hasPageLevelError(uiState.pageLevelError);
  if (hasError) {
    return;
  }

  const filteredFormState = filterFormState(formState);

  const { errorLogPayload, formErrors } = validateForm(filteredFormState);

  let isCRSFieldsValid = true;
  /**
   *  If CRS section is eligble, validate the CRS fields
   *  before submitting the form
   *  Kept Validation logic for CRS fields separate from
   *  validateForm since both of them differ in implementation
   */
  if (isEligibleForCRS()) {
    isCRSFieldsValid = validateCRSFormFields();
  }
  const fieldsWithErrors = Object.entries(formErrors);

  // If form is not valid, add errors to state and display them
  if (fieldsWithErrors.length || !isCRSFieldsValid) {
    const { value: accountType } = filteredFormState.onboardOption;
    const field_list = fieldsWithErrors.map(([field]) => field).join(',');
    logger.fpti('invalid_form_submission', {
      field_list: field_list,
      state_name: 'billing',
      transition_name: 'process_invalid_billing_page_submission',
      user_opts_signup: accountType === 'signup' ? 'Y' : 'N',
    });
    displayFormErrors(fieldsWithErrors, formErrors);
    logger.info('weasley_invalid_form_submission', errorLogPayload);

    // If form have just Residential Address errors we should show
    // Residential Address interstitial
    if (
      fieldsWithErrors &&
      fieldsWithErrors.length === 1 &&
      fieldsWithErrors[0][0] === 'residentialAddress'
    ) {
      uiActions.toggleIsResidentialAddressOpen(true);
    }

    CI.error(CI.ErrorReason.User);
    CI.endCurrent();
    atomics.markProblem({
      context: `${atomicsErrorContexts.invalidFormSubmission}: ${field_list}`,
      domain: atomicsDomain,
      reason: atomicsErrorReason.user,
      status: atomicsStatus.error,
    });
    return logger.flush();
  }

  // Prevent form from submission when signup optional upsell half-sheet
  // has to be displayed
  if (triggerMemberBenefitsTrueGuestsInterstitial()) {
    uiActions.toggleIsMemberBenefitsTrueGuestsInterstitialOpen();
    uiActions.toggleIsFormSubmitting(false);
    CI.endWith(CI.Event.GUEST_PAY_SUBMIT, CI.Status.Error, CI.ErrorReason.Flag);
    CI.start(CI.Event.CONTINGENCY_ACCOUNT_BENEFITS_USER_INPUT);
    atomics.markProblem({
      context: atomicsErrorContexts.memberBenefitsTrueGuest,
      domain: atomicsDomain,
      reason: atomicsErrorReason.flag,
      status: atomicsStatus.error,
    });
    return;
  }

  // Clear any existing page level error and show form submitting spinner
  uiActions.toggleIsFormSubmitting(true);
  uiActions.setPageLevelError('');
  uiActions.setPageWarningMessage('');

  // Check if guest can checkout as a member,
  // if so stop the form submission and display the modal
  const isEligibleForMemberBenefits =
    await checkMemberBenefits(filteredFormState);

  logCheckMemberBenefitsOccured(isEligibleForMemberBenefits);

  const memberMatchNoOptIn = features.isActive('isMemberMatchNoOptIn');
  const memberMatchNoToggleInterstitial = features.isActive(
    'isMemberMatchNoToggleWithInterstitial',
  );

  logFormSubmittedFunction({ isEligibleForMemberBenefits });
  // Don't double display member match half-sheet
  // and disclaimer if in treatment for no opt in.
  if (
    isEligibleForMemberBenefits &&
    (!memberMatchNoOptIn || memberMatchNoToggleInterstitial)
  ) {
    logger.info('member_benefits_eligible_interstitial_shown');
    uiActions.setIsMemberBenefitsInterstitialOpen(true);
    apiActions.setIsMemberCheckoutError(false);
    uiActions.toggleIsFormSubmitting(false);
    CI.endCurrent(CI.Status.Ok);
    return;
  }

  const hasAddressNormalization =
    features.isActive('hasAddressNormalizationShipping') ||
    features.isActive('hasAddressNormalizationBoth');
  const isAllowChangeShipping = features.isActive('isAllowChangeShipping');
  const { bypassAddressNormalization } = formState;
  const isServerSideShippingCallbacksEligible = features.isActive(
    'isServerSideShippingCallbacksEligible',
  );

  // Note: The `isServerSideShippingCallbacksEligible` check below is a temporary fix
  // for Weasley SSSC + address normalization compatibility issue.
  // TODO: long term fix https://paypal.atlassian.net/browse/DTMEMBER-3989
  const performAddressNormalization =
    !isServerSideShippingCallbacksEligible && hasAddressNormalization;
  logger.info('perform_address_normalization_flow', {
    hasAddressNormalization,
    isServerSideShippingCallbacksEligible,
  });

  // Check to see if normalization flow should be triggered
  if (performAddressNormalization) {
    if (isAllowChangeShipping && !bypassAddressNormalization) {
      logger.info('weasley_normalize_address');
      atomics.markProblem({
        context: atomicsErrorContexts.addressNormalization,
        domain: atomicsDomain,
        reason: atomicsErrorReason.user,
        status: atomicsStatus.error,
      });

      return await isAddressNormalized()
        .then(shouldCallOnboardAccount => {
          logger.info('weasley_normalize_address_final_response', {
            shouldCallOnboardAccount,
          });
          // TODO: Find out how todo CI on this...
          return shouldCallOnboardAccount && onboardAccount();
        })
        .catch(e => {
          // continue with out address normalization interstitial experience
          // if api file fails to load.
          logger.error('weasley_error_loading_normalize_address_api_file', e);
        });
    }

    logger.info('weasley_skip_address_normalization', {
      bypassAddressNormalization,
      hasAddressNormalization,
      isAllowChangeShipping,
    });
  }

  if (isEligibleForMemberBenefits && memberMatchNoOptIn) {
    return doCheckoutGuestAsMember(filteredFormState);
  }

  logOverCapturesDataOnSubmit();

  // Returning the Promise so we can await it in tests
  return onboardAccount(filteredFormState);
};

const onboardAccount = filteredFormState => {
  // In address normalization flow filteredFormState would not be passed as the
  // form state could change during the normalization flow.
  const { form: formState } = appStore.getState();
  if (!filteredFormState) {
    filteredFormState = filterFormState(formState);
  }

  if (shouldShowSCAPhoneConfirmationInterstitial()) {
    logger.fpti('phone_confirmation', {
      state_name: 'phone_confirmation',
      transition_name: 'display_sca_phone_confirmation_interstitial',
    });
    return;
  }

  // Returning the Promise so we can await it in tests
  return onboardAction(filteredFormState).then(data => {
    return data;
  });
};

const onboardingPaymentPlanHandler = () => {
  const { form: formState } = appStore.getState();
  const filteredFormState = filterFormState(formState);

  return createOnboardingPaymentPlan(filteredFormState)
    .then(threeDSHandler)
    .catch(handleApiError);
};

const doCheckoutGuestAsMember = filteredFormState => {
  // Switch the CI to member match
  CI.endCurrent(CI.Event.Ok);
  CI.start(CI.Event.MEMBER_MATCH_PAY_SUBMIT);

  const { ui: uiActions, api: apiActions } = appStore.getActions();
  const variables = serializeDataForMemberBenefits(filteredFormState);
  return checkoutGuestAsMember(variables)
    .then(threeDSHandler)
    .catch(() => {
      CI.endWith(
        CI.Event.MEMBER_MATCH_PAY_SUBMIT,
        CI.Status.Error,
        CI.ErrorReason.Server,
      );
      apiActions.setIsEligibleForMemberBenefits(false);
      apiActions.setIsMemberCheckoutError(true);
      uiActions.toggleIsFormSubmitting(false);
      uiActions.setIsMemberBenefitsInterstitialOpen(true);
    });
};

export {
  submitHandler,
  blurHandler,
  changeHandler,
  displayFormErrors,
  doCheckoutGuestAsMember,
  onboardAccount,
  onboardingPaymentPlanHandler,
  payLaterSubmitHandler,
  payLaterTermsCheckedHandler,
  payNowSubmitHandler,
};
